import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

export const scoreCoefficientsKeys = {
  all: ['scoreCoefficients'],
  lists: () => [...scoreCoefficientsKeys.all, 'list'],
  list: (filters) => [...scoreCoefficientsKeys.lists(), filters],
  details: () => [...scoreCoefficientsKeys.all, 'detail'],
  detail: (id) => [...scoreCoefficientsKeys.details(), id],
  last: () => [...scoreCoefficientsKeys.all, 'last'],
};

export const useScoreCoefficients = (filters = {}) => {
  return useQuery({
    queryKey: scoreCoefficientsKeys.list(filters),
    queryFn: async () => {
      const response = await apiClient.get('score_coefficients', {
        params: filters,
      });
      return response.data;
    },
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useScoreCoefficient = (id, options = {}) => {
  return useQuery({
    queryKey: scoreCoefficientsKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`score_coefficients/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const useLastScoreCoefficient = () => {
  return useQuery({
    queryKey: scoreCoefficientsKeys.last(),
    queryFn: async () => {
      const response = await apiClient.get('score_coefficients/last');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useDeleteScoreCoefficient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`score_coefficients/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: scoreCoefficientsKeys.lists() });
      queryClient.removeQueries({ queryKey: scoreCoefficientsKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting score coefficient:', error);
    },
  });
};

export const useAddScoreCoefficient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scoreCoefficient) => {
      const response = await apiClient.post('score_coefficients', scoreCoefficient);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scoreCoefficientsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scoreCoefficientsKeys.last() });
    },
    onError: (error) => {
      console.error('Error adding score coefficient:', error);
    },
  });
};

export const useUpdateScoreCoefficient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await apiClient.put(`score_coefficients/${id}`, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(scoreCoefficientsKeys.detail(variables.id), data);
      queryClient.invalidateQueries({ queryKey: scoreCoefficientsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scoreCoefficientsKeys.last() });
    },
    onError: (error) => {
      console.error('Error updating score coefficient:', error);
    },
  });
};
